import { useEffect, useMemo, useState } from "react";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import { DownloadListContainer } from "../../../style/contents/backup/backup";
import DownloadList from "../../../components/download/download.list";
import { BackupUser } from "../backup/backup.user";
import {
  fetchBackupDeviceListAdmin,
  fetchBackupUserListLoad,
  fetchLoadDownloadFileList,
} from "../../../api/admin";
import { DeviceItem } from "../backup/backup.device.list";
import { useForm } from "react-hook-form";

// form 타입 정의
type FormValues = {
  userId: number;
  deviceId: string;
};

export interface DownloadFileItem {
  backupId: number;
  userId?: number;
  name: string; // zip 파일 이름
  targetPath: string; // 백업 대상 경로 (전체 경로)
  state: "completed" | "buffering"; // 상태 (완료 or 버퍼링)
  createdDate: string; // 생성일시 (ISO 포맷 문자열)
  userAccount: string;
  deviceId: string;
}

const DownloadFileList = () => {
  const [data, setData] = useState<DownloadFileItem[]>([]);
  const [userList, setUserList] = useState<BackupUser[]>([]); // ✅ 사용자 리스트 상태
  const [selectedUser, setSelectedUser] = useState<BackupUser | null>(null); // ✅ 선택된 사용자
  const [deviceOptions, setDeviceOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);

  const { handleSubmit, setValue, watch } = useForm<FormValues>();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    // TODO: fetchData(keyword, pageSize);
  };

  const handlePageSizeChange = (selectedOption: any) => {
    // 넘기는 객체가 잘못되어 현재 동작안함. 추후 수정 예정
    // 컴포넌트를 분리할 수도 있음.
    if (selectedOption && typeof selectedOption === "object") {
      const newSize = selectedOption.value;
      setPageSize(newSize);
    } else {
      console.warn("❌ 예상치 못한 selectedOption 값:", selectedOption);
    }
  };

  const handleUserChange = async (selectedOption: any) => {
    const user = userList.find((u) => u.userId === selectedOption?.value);
    setSelectedUser(user ?? null);
    setSelectedDevice(null);
    setValue("userId", selectedOption?.value); // ✅ useForm 값 설정

    if (user) {
      try {
        const adminUserId = localStorage.getItem("userId");
        const response = await fetchBackupDeviceListAdmin(Number(adminUserId));

        if (response?.userList) {
          const filteredDevices = response.userList.filter(
            (d: DeviceItem) => d.userId === user.userId
          );

          const options = filteredDevices.map((device: DeviceItem) => ({
            value: device.deviceId,
            label: device.deviceId,
          }));
          setDeviceOptions(options);
        }
      } catch (err) {
        console.error("🚨 디바이스 불러오기 실패:", err);
      }
    } else {
      setDeviceOptions([]);
    }
  };

  const handleDeviceChange = (selectedOption: any) => {
    setSelectedDevice(selectedOption?.value ?? null);
    setValue("deviceId", selectedOption?.value ?? "");
  };

  // ✅ 사용자 목록 로딩
  useEffect(() => {
    const loadUserList = async () => {
      const userId = localStorage.getItem("userId");
      const companyCode = localStorage.getItem("companyCode");

      const response = await fetchBackupUserListLoad(
        Number(userId),
        companyCode || ""
      );
      setUserList(response.userList);
    };

    loadUserList();
  }, []);

  const userSelectOptions = userList.map((user) => ({
    value: user.userId,
    label: user.userAccount,
  }));

  const onSubmit = async (data: FormValues) => {
    // const adminUserId = localStorage.getItem("userId");
    // if (!adminUserId || !data.userId || !data.deviceId) {
    //   alert("사용자와 디바이스를 모두 선택해주세요.");
    //   return;
    // }
    // try {
    //   setIsLoading(true); // ✅ 로딩 시작
    //   const response = await fetchLoadDownloadFileList(
    //     adminUserId,
    //     data.userId.toString(),
    //     data.deviceId
    //   );
    //   console.log("📥 다운로드 목록:", response);
    //   setData(response.zipFileList);
    //   setIsFetched(true); // ✅ 불러오기 완료
    // } catch (err) {
    //   console.error("🚨 다운로드 목록 불러오기 실패:", err);
    //   alert("다운로드 목록을 불러오는데 실패했습니다.");
    // } finally {
    //   setIsLoading(false); // ✅ 로딩 종료
    // }
  };

  useEffect(() => {
    const loadFileList = async () => {
      const adminUserId = localStorage.getItem("userId");
      const userId = "1"; // 수정되기 전까지는 아무값이나
      const deviceId = "2"; // 수정되기 전까지는 아무값이나

      try {
        setIsLoading(true); // ✅ 로딩 시작

        const response = await fetchLoadDownloadFileList(
          adminUserId ?? "",
          userId,
          deviceId
        );
        console.log("📥 다운로드 목록:", response);
        setData(response.zipFileList);
        setIsFetched(true); // ✅ 불러오기 완료
      } catch (err) {
        console.error("🚨 다운로드 목록 불러오기 실패:", err);
        alert("다운로드 목록을 불러오는데 실패했습니다.");
      } finally {
        setIsLoading(false); // ✅ 로딩 종료
      }
    };

    loadFileList();
  }, []);

  const filteredData = useMemo(() => {
    if (!searchKeyword) return data;

    const keyword = searchKeyword.toLowerCase().replace(/[^a-z0-9]/gi, ""); // 특수문자 제거

    return data.filter((item) => {
      const name = item.name.toLowerCase();
      const path = item.targetPath.toLowerCase();
      const stateKor =
        item.state === "completed"
          ? "완료"
          : item.state === "buffering"
          ? "진행중"
          : "";
      const stateEng = item.state.toLowerCase();

      // 날짜에서 숫자만 추출해서 비교용 문자열 생성
      const rawDate = item.createdDate.slice(0, 10); // '2025-07-11'
      const dateNumeric = rawDate.replace(/-/g, ""); // '20250711'

      return (
        name.includes(keyword) ||
        path.includes(keyword) ||
        stateKor.includes(keyword) ||
        stateEng.includes(keyword) ||
        dateNumeric.includes(keyword)
      );
    });
  }, [data, searchKeyword]);

  return (
    <DownloadListContainer>
      {" "}
      <ContentsTopHeader
        title="백업 다운로드 목록"
        onSearch={handleSearch}
        onPageSizeChange={handlePageSizeChange}
        className="user-list-top-header"
      />
      <div className="contents-body">
        <div className="user-select-info">
          {/* <form
            onSubmit={handleSubmit(onSubmit)}
            className="backup-download-header-box"
          >
            <Select
              className="select-contents"
              classNamePrefix="react-select"
              options={userSelectOptions}
              onChange={handleUserChange}
              value={
                selectedUser
                  ? {
                      value: selectedUser.userId,
                      label: selectedUser.userAccount,
                    }
                  : null
              }
              placeholder="사용자 선택"
              components={{ IndicatorSeparator: () => null }}
            />
            <Select
              className="select-contents"
              classNamePrefix="react-select"
              options={deviceOptions}
              onChange={handleDeviceChange}
              value={
                selectedDevice
                  ? deviceOptions.find((opt) => opt.value === selectedDevice)
                  : null
              }
              placeholder="디바이스 선택"
              components={{ IndicatorSeparator: () => null }}
            />
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              className="select-btn"
            >
              불러오기
            </button>
          </form> */}
        </div>

        <div className="download-list-box">
          <DownloadList
            data={filteredData}
            pageSize={Number(pageSize)}
            userId={selectedUser?.userId?.toString() || ""}
            deviceId={selectedDevice || ""}
            isLoading={isLoading} // ✅ 전달
            isFetched={isFetched} // ✅ 추가
          />
        </div>
      </div>
    </DownloadListContainer>
  );
};

export default DownloadFileList;
